import React, { useState, useMemo, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
} from 'recharts';
import type { StudentRecord, ComparisonAttribute } from '../types';
import { aggregateByAttribute, ComparisonGroupData } from '../utils/comparisonUtils';

/* ── Sub-tab definitions ── */
const COMPARISON_TABS: { id: ComparisonAttribute; label: string }[] = [
  { id: 'overall',     label: 'Overall' },
  { id: 'sponsoredBy', label: 'Sponsored By' },
  { id: 'gender',      label: 'Gender' },
  { id: 'category',    label: 'Category' },
  { id: 'urbanRural',  label: 'Urban / Rural' },
  { id: 'cwsn',        label: 'CWSN' },
  { id: 'pmShri',      label: 'PM Shri' },
  { id: 'stream',      label: 'Stream' },
];

/* ── Color palette for groups ── */
const GROUP_COLORS = [
  '#F36B21', '#3b82f6', '#22c55e', '#8b5cf6', '#ec4899',
  '#f59e0b', '#06b6d4', '#ef4444', '#14b8a6', '#a855f7',
];

interface Props {
  students: StudentRecord[];
  title?: string;
  availableTabs?: ComparisonAttribute[];
  apiComparison?: {
    overall?: ComparisonGroupData;
    sponsoredBy?: ComparisonGroupData[];
    gender?: ComparisonGroupData[];
    category?: ComparisonGroupData[];
    urbanRural?: ComparisonGroupData[];
    cwsn?: ComparisonGroupData[];
    pmShri?: ComparisonGroupData[];
    stream?: ComparisonGroupData[];
  };
}

/* ── Custom Tooltip ── */
const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg text-sm">
      <p className="font-semibold text-gray-800 mb-1">{label}</p>
      {payload.map((p: any) => (
        <p key={p.dataKey} style={{ color: p.color }}>
          {p.name}: <span className="font-bold">{(p.value ?? 0).toLocaleString()}</span>
        </p>
      ))}
    </div>
  );
};

const ComparisonAnalysisPanel: React.FC<Props> = ({
  students,
  title = 'Comparison Analysis',
  availableTabs,
  apiComparison,
}) => {
  const [activeTab, setActiveTab] = useState<ComparisonAttribute>('overall');

  const visibleTabs = useMemo(() => {
    if (!availableTabs || availableTabs.length === 0) {
      return COMPARISON_TABS;
    }

    const allowed = new Set(availableTabs);
    return COMPARISON_TABS.filter((tab) => allowed.has(tab.id));
  }, [availableTabs]);

  useEffect(() => {
    if (!visibleTabs.some((tab) => tab.id === activeTab)) {
      setActiveTab('overall');
    }
  }, [visibleTabs, activeTab]);

  const groupData: ComparisonGroupData[] = useMemo(
    () => {
      if (activeTab === 'overall' && apiComparison?.overall) {
        return [apiComparison.overall];
      }

      if (activeTab === 'gender' && apiComparison?.gender) {
        return apiComparison.gender;
      }

      if (activeTab === 'category' && apiComparison?.category) {
        return apiComparison.category;
      }

      if (activeTab === 'sponsoredBy' && apiComparison?.sponsoredBy) {
        return apiComparison.sponsoredBy;
      }

      if (activeTab === 'urbanRural' && apiComparison?.urbanRural) {
        return apiComparison.urbanRural;
      }

      if (activeTab === 'cwsn' && apiComparison?.cwsn) {
        return apiComparison.cwsn;
      }

      if (activeTab === 'pmShri' && apiComparison?.pmShri) {
        return apiComparison.pmShri;
      }

      if (activeTab === 'stream' && apiComparison?.stream) {
        return apiComparison.stream;
      }

      const aggregated = aggregateByAttribute(students, activeTab);
      if (aggregated.length > 0) {
        return aggregated;
      }

      return [{ groupName: 'No Data', totalStudents: 0, passed: 0, passPercentage: 0, performanceIndex: 0 }];
    },
    [students, activeTab, apiComparison],
  );

  /* Bar-chart data: one entry per group showing pass% and average percentage */
  const chartData = useMemo(
    () =>
      groupData.map((g) => ({
        name: g.groupName,
        'Pass %': g.passPercentage,
        'Perf. Index': g.performanceIndex,
        totalStudents: g.totalStudents,
        passed: g.passed,
      })),
    [groupData],
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* ── Header ── */}
      <div className="px-6 pt-5 pb-3">
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        <p className="text-xs text-gray-400 mt-0.5">
          Compare pass rate &amp; average percentage across dimensions
        </p>
      </div>

      {/* ── Sub-tabs ── */}
      <div className="px-6 pb-1 flex flex-wrap gap-1.5">
        {visibleTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-[#F36B21] text-white shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Chart ── */}
      <div className="px-6 pt-4">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={chartData}
            margin={{ top: 10, right: 20, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11, fill: '#666' }}
              tickLine={false}
              axisLine={{ stroke: '#e5e7eb' }}
              interval={0}
              angle={chartData.length > 5 ? -25 : 0}
              textAnchor={chartData.length > 5 ? 'end' : 'middle'}
              height={chartData.length > 5 ? 60 : 30}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#666' }}
              tickLine={false}
              axisLine={{ stroke: '#e5e7eb' }}
            />
            <Tooltip content={<ChartTooltip />} />
            <Legend iconType="circle" wrapperStyle={{ paddingTop: '8px' }} />
            <Bar dataKey="Pass %" radius={[4, 4, 0, 0]} maxBarSize={48} name="Pass %">
              {chartData.map((_, i) => (
                <Cell key={i} fill={GROUP_COLORS[i % GROUP_COLORS.length]} />
              ))}
            </Bar>
            <Bar dataKey="Perf. Index" radius={[4, 4, 0, 0]} maxBarSize={48} name="Perf. Index" fillOpacity={0.6}>
              {chartData.map((_, i) => (
                <Cell key={i} fill={GROUP_COLORS[i % GROUP_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* ── Summary Table ── */}
      <div className="px-6 pb-5 pt-2 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 uppercase">Group</th>
              <th className="text-right py-2.5 px-3 text-xs font-semibold text-gray-500 uppercase">Total Students</th>
              <th className="text-right py-2.5 px-3 text-xs font-semibold text-gray-500 uppercase">Passed</th>
              <th className="text-right py-2.5 px-3 text-xs font-semibold text-gray-500 uppercase">Pass %</th>
              <th className="text-right py-2.5 px-3 text-xs font-semibold text-gray-500 uppercase">Perf. Index</th>
            </tr>
          </thead>
          <tbody>
            {groupData.map((g, i) => (
              <tr
                key={g.groupName}
                className="border-b border-gray-100 hover:bg-gray-50/60 transition"
              >
                <td className="py-2.5 px-3 font-medium text-gray-800 flex items-center gap-2">
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: GROUP_COLORS[i % GROUP_COLORS.length] }}
                  />
                  {g.groupName}
                </td>
                <td className="py-2.5 px-3 text-right text-gray-700">{(g.totalStudents ?? 0).toLocaleString()}</td>
                <td className="py-2.5 px-3 text-right text-gray-700">{(g.passed ?? 0).toLocaleString()}</td>
                <td className="py-2.5 px-3 text-right font-semibold text-green-700">{g.passPercentage}%</td>
                <td className="py-2.5 px-3 text-right font-semibold text-blue-700">{g.performanceIndex}</td>
              </tr>
            ))}
            {groupData.length === 0 && (
              <tr>
                <td colSpan={5} className="py-6 text-center text-gray-400">No data available</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ComparisonAnalysisPanel;
